import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useUrlSearchParams, urlNumber } from './useUrlSearchParams';
import type { UrlParamDefinition, InferUrlParams } from './useUrlSearchParams';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * `useUrlPagination` hook'unun yapılandırma seçenekleri.
 *
 * @template TFilters - Ek filtre parametrelerinin şema tipi (opsiyonel).
 *   Verilirse pagination parametrelerine ek olarak bu filtreler de URL'de saklanır.
 */
export interface UseUrlPaginationOptions<TFilters extends Record<string, UrlParamDefinition> = Record<string, never>> {
  /** Varsayılan sayfa numarası (1-based). Varsayılan: 1 */
  defaultPage?: number;
  /** Varsayılan sayfa başına kayıt sayısı. Varsayılan: 10 */
  defaultLimit?: number;
  /** URL'deki page parametresinin adı. Varsayılan: 'page' */
  pageParam?: string;
  /** URL'deki limit parametresinin adı. Varsayılan: 'limit' */
  limitParam?: string;

  /**
   * Toplam sayfa sayısı.
   * Ayarlandığında, URL'den okunan page değeri bu sayıyı aşarsa
   * `onInvalidPage` callback'i çağrılır.
   * Undefined/null ise doğrulama yapılmaz.
   */
  maxPage?: number | null;

  /**
   * `maxPage` aşıldığında çağrılır.
   * Varsayılan davranış: sayfa 1'e reset eder.
   * Custom davranış istiyorsanız bu callback'i override edin.
   *
   * @param requestedPage - URL'de bulunan geçersiz sayfa numarası
   * @param maxPage - İzin verilen maksimum sayfa numarası
   */
  onInvalidPage?: (requestedPage: number, maxPage: number) => void;

  /**
   * Browser back/forward navigasyonunda (POP) çağrılır.
   * Programatik URL değişikliklerinde (`setPage`, `setLimit` vb.) çağrılmaz.
   * Component'in data'yı yeniden fetch etmesi için kullanılır.
   *
   * @param page - POP sonrası sayfa numarası (1-based)
   * @param limit - POP sonrası sayfa boyutu
   * @param filters - POP sonrası filtre değerleri (TFilters tanımlandıysa)
   */
  onPopState?: (page: number, limit: number, filters: TFilters extends Record<string, never> ? undefined : InferUrlParams<TFilters>) => void;

  /**
   * Pagination'a ek olarak URL'de saklanacak filtre parametreleri.
   * Her key bir URL search parametresine karşılık gelir.
   *
   * @example
   * ```ts
   * filters: {
   *   search:   urlString(),
   *   category: urlCommaSeparated(),
   *   sort:     urlString('newest'),
   * }
   * ```
   */
  filters?: TFilters;
}

/**
 * `useUrlPagination` hook'unun dönüş tipi.
 *
 * @template TFilters - Ek filtre parametrelerinin şema tipi
 */
export interface UseUrlPaginationReturn<TFilters extends Record<string, UrlParamDefinition> = Record<string, never>> {
  /** Mevcut sayfa numarası (1-based) */
  page: number;
  /** Sayfa başına kayıt sayısı */
  limit: number;

  /**
   * Sayfa numarasını günceller ve URL'e yansıtır.
   *
   * @param page - Yeni sayfa numarası (1-based, minimum 1)
   */
  setPage: (page: number) => void;

  /**
   * Sayfa başına kayıt sayısını günceller, sayfayı 1'e sıfırlar ve URL'e yansıtır.
   * Not: Mevcut filtreler korunur.
   *
   * @param limit - Yeni sayfa boyutu (minimum 1)
   */
  setLimit: (limit: number) => void;

  /**
   * Hem sayfa hem limit'i tek seferde günceller ve URL'e yansıtır.
   *
   * @param page - Yeni sayfa numarası (1-based)
   * @param limit - Yeni sayfa boyutu
   */
  setPagination: (page: number, limit: number) => void;

  /** Pagination'ı varsayılan değerlere sıfırlar (filtreler de sıfırlanır). */
  resetPagination: () => void;

  /**
   * Mevcut filtre değerleri (schema'dan parse edilmiş).
   * `filters` option'ı verilmediyse boş obje döner.
   */
  filterValues: TFilters extends Record<string, never> ? Record<string, never> : InferUrlParams<TFilters>;

  /**
   * Tek bir filtre parametresini günceller ve URL'e yansıtır.
   * Sayfa otomatik olarak 1'e sıfırlanır.
   *
   * @param key - Güncellenecek filtre adı
   * @param value - Yeni değer
   */
  setFilter: <K extends keyof TFilters & string>(key: K, value: TFilters[K] extends UrlParamDefinition<infer V> ? V : never) => void;

  /**
   * Birden fazla filtre parametresini tek seferde günceller ve URL'e yansıtır.
   * Sayfa otomatik olarak 1'e sıfırlanır.
   * Verilmeyen filtreler mevcut değerlerini korur.
   *
   * @param partial - Güncellenecek filtre key-value çiftleri
   */
  setFilters: (partial: Partial<InferUrlParams<TFilters>>) => void;

  /** Tüm filtreleri varsayılan değerlere sıfırlar ve sayfayı 1'e döndürür. */
  resetFilters: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Pagination ve filtre state'ini URL search params ile senkronize eden hook.
 *
 * `useUrlSearchParams` üzerine inşa edilmiştir. Pagination'a özgü
 * davranışları ekler: `setLimit`'te sayfa reset, `setFilter`'da sayfa reset,
 * `maxPage` doğrulaması, `onInvalidPage` callback'i.
 *
 * @template TFilters - Ek filtre parametrelerinin şema tipi (opsiyonel)
 *
 * @example
 * ```tsx
 * // Sadece pagination (filtre yok)
 * const { page, limit, setPage, setLimit } = useUrlPagination({
 *   onPopState: (page, limit) => {
 *     dispatch(fetchData({ page, limit }));
 *   },
 * });
 * ```
 *
 * @example
 * ```tsx
 * // Pagination + filtreler
 * const { page, limit, filterValues, setFilter, setFilters, setPage } = useUrlPagination({
 *   filters: {
 *     search:   urlString(),
 *     category: urlCommaSeparated(),
 *     sort:     urlString(),
 *   },
 *   maxPage: totalPageCount,
 *   onPopState: (page, limit, filters) => {
 *     dispatch(fetchData({ page, limit, ...filters }));
 *   },
 * });
 *
 * // Filtre değiştiğinde sayfa otomatik 1'e döner
 * setFilter('search', 'laptop');
 *
 * // Birden fazla filtre
 * setFilters({ search: 'laptop', category: ['electronics'] });
 * ```
 */
export function useUrlPagination<TFilters extends Record<string, UrlParamDefinition> = Record<string, never>>(
  options: UseUrlPaginationOptions<TFilters> = {} as UseUrlPaginationOptions<TFilters>,
): UseUrlPaginationReturn<TFilters> {
  const { defaultPage = 1, defaultLimit = 10, pageParam = 'page', limitParam = 'limit', maxPage, onInvalidPage, onPopState, filters: filterSchema } = options;

  // Ref'ler: stale closure önlemi
  const onPopStateRef = useRef(onPopState);
  onPopStateRef.current = onPopState;

  const onInvalidPageRef = useRef(onInvalidPage);
  onInvalidPageRef.current = onInvalidPage;

  // -----------------------------------------------------------------------
  // Schema oluştur: pagination + opsiyonel filtreler
  // -----------------------------------------------------------------------
  const schema = useMemo(() => {
    const base: Record<string, UrlParamDefinition> = {
      [pageParam]: urlNumber(defaultPage),
      [limitParam]: urlNumber(defaultLimit),
    };

    if (filterSchema) {
      Object.assign(base, filterSchema);
    }

    return base;
  }, [pageParam, limitParam, defaultPage, defaultLimit, filterSchema]);

  // -----------------------------------------------------------------------
  // useUrlSearchParams ile URL senkronizasyonu
  // -----------------------------------------------------------------------
  const { params, setParams, resetParams } = useUrlSearchParams({
    schema,
    writeDefaultsToUrl: true,
    onPopState: (popParams) => {
      const popPage = popParams[pageParam] as number;
      const popLimit = popParams[limitParam] as number;

      // Filtre değerlerini ayır
      const popFilters = { ...popParams };
      delete popFilters[pageParam];
      delete popFilters[limitParam];

      onPopStateRef.current?.(popPage, popLimit, (filterSchema ? popFilters : undefined) as any);
    },
  });

  const rawPage = params[pageParam] as number;
  const limit = params[limitParam] as number;

  // -----------------------------------------------------------------------
  // maxPage doğrulaması
  // -----------------------------------------------------------------------
  // URL'deki ham sayfa hâlâ geçersiz olabilir (ör. page=5, maxPage=4). Tüketicilere
  // her zaman geçerli `page` döndür ki aynı turdaki useEffect'ler yanlış sayfayla
  // istek atmasın. URL'i useLayoutEffect ile hemen düzelt (paint öncesi).
  const pageOutOfMaxRange = maxPage != null && maxPage >= 1 && rawPage > maxPage;
  const page = pageOutOfMaxRange ? defaultPage : rawPage;

  useLayoutEffect(() => {
    if (maxPage == null || maxPage < 1) {
      return;
    }
    if (rawPage <= maxPage) {
      return;
    }
    if (onInvalidPageRef.current) {
      onInvalidPageRef.current(rawPage, maxPage);
      return;
    }
    setParams({ [pageParam]: defaultPage } as any);
  }, [rawPage, maxPage, defaultPage, pageParam, setParams]);

  // -----------------------------------------------------------------------
  // Filtre değerlerini ayır
  // -----------------------------------------------------------------------
  const filterValues = useMemo(() => {
    const result = { ...params };
    delete result[pageParam];
    delete result[limitParam];
    return result;
  }, [params, pageParam, limitParam]) as any;

  // -----------------------------------------------------------------------
  // Public API: Pagination
  // -----------------------------------------------------------------------

  const setPage = useCallback(
    (newPage: number) => {
      setParams({ [pageParam]: Math.max(1, newPage) } as any);
    },
    [setParams, pageParam],
  );

  const setLimit = useCallback(
    (newLimit: number) => {
      // Limit değiştiğinde sayfayı 1'e reset et (filtreler korunur)
      setParams({
        [pageParam]: defaultPage,
        [limitParam]: Math.max(1, newLimit),
      } as any);
    },
    [setParams, pageParam, limitParam, defaultPage],
  );

  const setPagination = useCallback(
    (newPage: number, newLimit: number) => {
      setParams({
        [pageParam]: Math.max(1, newPage),
        [limitParam]: Math.max(1, newLimit),
      } as any);
    },
    [setParams, pageParam, limitParam],
  );

  const resetPagination = useCallback(() => {
    resetParams();
  }, [resetParams]);

  // -----------------------------------------------------------------------
  // Public API: Filters
  // -----------------------------------------------------------------------

  const setFilter = useCallback(
    <K extends keyof TFilters & string>(key: K, value: TFilters[K] extends UrlParamDefinition<infer V> ? V : never) => {
      // Filtre değiştiğinde sayfayı 1'e reset et
      setParams({
        [pageParam]: defaultPage,
        [key]: value,
      } as any);
    },
    [setParams, pageParam, defaultPage],
  );

  const setFilters = useCallback(
    (partial: Partial<InferUrlParams<TFilters>>) => {
      // Filtre değiştiğinde sayfayı 1'e reset et
      setParams({
        [pageParam]: defaultPage,
        ...partial,
      } as any);
    },
    [setParams, pageParam, defaultPage],
  );

  const resetFilters = useCallback(() => {
    if (!filterSchema) return;

    const defaults: Record<string, any> = { [pageParam]: defaultPage };
    for (const key of Object.keys(filterSchema)) {
      defaults[key] = filterSchema[key].defaultValue;
    }

    setParams(defaults as any);
  }, [setParams, filterSchema, pageParam, defaultPage]);

  // -----------------------------------------------------------------------
  // Return
  // -----------------------------------------------------------------------
  return {
    page,
    limit,
    setPage,
    setLimit,
    setPagination,
    resetPagination,
    filterValues,
    setFilter,
    setFilters,
    resetFilters,
  };
}
